// src/components/stock/UnifiedStockTab.js
/**
 * Unified Stock Tab Component
 * 
 * Displays consolidated view of all products in stock with calculated quantities.
 * Products in PackProductsMaster get their pack_size, others default to 1.
 * calculated_qty = raw_stock × pack_size
 */

import React, { useEffect, useState, useCallback } from 'react';
import { 
    Table, Button, Tag, Space, Input, Select, message, 
    Tooltip, Checkbox, Spin, Empty, Modal, Form, InputNumber
} from 'antd';
import { 
    ReloadOutlined, SearchOutlined, DatabaseOutlined,
    CheckCircleOutlined, InboxOutlined, ShoppingOutlined,
    ClearOutlined, EditOutlined
} from '@ant-design/icons';
import axios from 'axios';
import { NODE_BACKEND_API_URL } from '../../config';
import packProductsService from '../../services/packProductsService';
import './UnifiedStockTab.css';

const isDemoMode = process.env.REACT_APP_DEMO_MODE === 'true';

const { Option } = Select;

const UnifiedStockTab = () => {
    // Data state
    const [products, setProducts] = useState([]);
    const [stats, setStats] = useState(null);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(false);
    const [statsLoading, setStatsLoading] = useState(false);
    
    // Pagination
    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 50,
        total: 0,
    });
    
    // Filters
    const [filters, setFilters] = useState({
        search: '',
        brand: '',
        sortBy: 'calculated_qty',
        sortOrder: 'DESC',
        includeZeroStock: false,
        productTypeFilter: 'all', // 'all', 'pack', 'single'
        verifiedOnly: false,
    });
    
    const [searchInput, setSearchInput] = useState('');
    const [brandInput, setBrandInput] = useState('');
    
    // Edit Product Modal state
    const [editModalVisible, setEditModalVisible] = useState(false);
    const [editingProduct, setEditingProduct] = useState(null);
    const [editLoading, setEditLoading] = useState(false);
    const [editForm] = Form.useForm();

    // Load initial data
    useEffect(() => {
        loadStats();
    }, []);

    // Load products when filters or pagination changes
    useEffect(() => {
        loadProducts();
    }, [filters, pagination.current, pagination.pageSize]);

    // Load stats
    const loadStats = async () => {
        setStatsLoading(true);
        try {
            const result = await packProductsService.getUnifiedStockStats();
            if (result.success) {
                setStats(result.data);
            }
        } catch (error) {
            console.error('Error loading stats:', error);
            message.error('Error al cargar estadísticas');
        } finally {
            setStatsLoading(false);
        }
    };

    // Load products
    const loadProducts = async () => {
        setLoading(true);
        try {
            const params = {
                page: pagination.current,
                pageSize: pagination.pageSize,
                sortBy: filters.sortBy,
                sortOrder: filters.sortOrder,
                includeZeroStock: filters.includeZeroStock,
                packProductsOnly: filters.productTypeFilter === 'pack',
                nonPackOnly: filters.productTypeFilter === 'single',
                verifiedOnly: filters.verifiedOnly,
            };
            
            if (filters.search) params.search = filters.search;
            if (filters.brand) params.brand = filters.brand;

            const result = await packProductsService.getUnifiedStock(params);
            
            if (result.success) {
                setProducts(result.data.products);
                setSummary(result.data.summary);
                setPagination(prev => ({
                    ...prev,
                    total: result.data.pagination.totalItems,
                }));
            }
        } catch (error) {
            console.error('Error loading products:', error);
            message.error('Error al cargar productos');
        } finally {
            setLoading(false);
        }
    };

    // Handle search submit
    const handleSearch = () => {
        setFilters(prev => ({
            ...prev,
            search: searchInput,
            brand: brandInput,
        }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle filter change
    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Clear all filters
    const handleClearFilters = () => {
        setSearchInput('');
        setBrandInput('');
        setFilters({
            search: '',
            brand: '',
            sortBy: 'calculated_qty',
            sortOrder: 'DESC',
            includeZeroStock: false,
            productTypeFilter: 'all',
            verifiedOnly: false,
        });
        setPagination(prev => ({ ...prev, current: 1 }));
    };

    // Handle table pagination and sorting change
    const handleTableChange = (paginationConfig, _, sorter) => {
        setPagination(prev => ({
            ...prev,
            current: paginationConfig.current,
            pageSize: paginationConfig.pageSize,
        }));
        
        if (sorter.field) {
            setFilters(prev => ({
                ...prev,
                sortBy: sorter.field,
                sortOrder: sorter.order === 'ascend' ? 'ASC' : 'DESC',
            }));
        }
    };

    // Format large numbers
    const formatNumber = (num) => {
        if (num === null || num === undefined) return '-';
        return num.toLocaleString('es-AR');
    };

    // Format large numbers with abbreviation
    const formatLargeNumber = (num) => {
        if (num === null || num === undefined) return '0';
        if (Math.abs(num) >= 1000000) {
            return (num / 1000000).toFixed(1) + 'M';
        }
        if (Math.abs(num) >= 1000) {
            return (num / 1000).toFixed(1) + 'K';
        }
        return num.toLocaleString('es-AR');
    };

    // Get stock class
    const getStockClass = (stock) => {
        if (stock < 0) return 'negative';
        if (stock > 0) return 'positive';
        return 'zero';
    };

    // Get row class name
    const getRowClassName = (record) => {
        if (record.raw_stock < 0 || record.calculated_qty < 0) return 'row-negative-stock';
        if (record.raw_stock === 0) return 'row-zero-stock';
        return '';
    };

    // --- Edit Product Modal Handlers ---
    
    // Open edit modal with product data - fetch complete details from API
    const handleEditProduct = async (record) => {
        setEditingProduct(record);
        setEditModalVisible(true);
        setEditLoading(true);
        
        try {
            // Fetch complete product details from API
            const response = await axios.get(
                `${NODE_BACKEND_API_URL}/getproductbyid/${record.product_id}`
            );
            const productDetails = response.data;
            
            // Merge API data with record data
            const completeProduct = {
                ...record,
                brand_id: productDetails.BrandId,
                ean: productDetails.EAN || '',
                product_cost: productDetails.ProductCost || 0,
            };
            
            setEditingProduct(completeProduct);
            editForm.setFieldsValue({
                product_code: record.product_code,
                product_description: record.product_description,
                brand_name: record.brand_name,
                raw_stock: record.raw_stock,
                pack_size: record.pack_size || 1,
            });
        } catch (error) {
            console.error('Error fetching product details:', error);
            message.warning('No se pudieron cargar todos los detalles. Algunos campos pueden estar incompletos.');
            // Still allow editing with available data
            editForm.setFieldsValue({
                product_code: record.product_code,
                product_description: record.product_description,
                brand_name: record.brand_name,
                raw_stock: record.raw_stock,
                pack_size: record.pack_size || 1,
            });
        } finally {
            setEditLoading(false);
        }
    };

    // Close edit modal
    const handleEditModalClose = () => {
        setEditModalVisible(false);
        setEditingProduct(null);
        editForm.resetFields();
    };

    // Submit edit form
    const handleEditSubmit = async () => {
        try {
            const values = await editForm.validateFields();
            setEditLoading(true);

            // Prepare data for the backend - must include all required fields
            const updateData = {
                brandId: editingProduct.brand_id,
                productCode: editingProduct.product_code,
                productDescription: values.product_description,
                ean: editingProduct.ean || '',
                productCost: editingProduct.product_cost || 0,
                quantity: parseInt(values.raw_stock, 10),
            };

            // Call the update endpoint
            const response = await axios.post(
                `${NODE_BACKEND_API_URL}/postupdateproduct/${editingProduct.product_id}`,
                updateData
            );

            if (response.data) {
                message.success(`Producto "${editingProduct.product_code}" actualizado correctamente`);
                handleEditModalClose();
                // Refresh the table data
                loadProducts();
                loadStats();
            }
        } catch (error) {
            console.error('Error updating product:', error);
            if (error.response) {
                message.error(error.response.data?.msg || 'Error al actualizar el producto');
            } else if (error.errorFields) {
                message.warning('Por favor complete los campos requeridos');
            } else {
                message.error('Error de conexión al actualizar el producto');
            }
        } finally {
            setEditLoading(false);
        }
    };

    // Table columns
    const columns = [
        {
            title: 'Código',
            dataIndex: 'product_code',
            key: 'product_code',
            width: 120,
            sorter: true,
            render: (code) => <span className="code-cell">{code}</span>,
        },
        {
            title: 'Descripción',
            dataIndex: 'product_description',
            key: 'product_description',
            ellipsis: true,
            sorter: true,
        },
        {
            title: 'Marca',
            dataIndex: 'brand_name',
            key: 'brand_name',
            width: 120,
            sorter: true,
            render: (brand) => <Tag color="blue">{brand || 'SIN MARCA'}</Tag>,
        },
        {
            title: 'Stock Original',
            dataIndex: 'raw_stock',
            key: 'raw_stock',
            width: 130,
            sorter: true,
            align: 'right',
            render: (stock) => (
                <span className={`stock-value ${getStockClass(stock)}`}>
                    {formatNumber(stock)}
                </span>
            ),
        },
        {
            title: 'Tamaño Pack',
            dataIndex: 'pack_size',
            key: 'pack_size',
            width: 130,
            sorter: true,
            align: 'center',
            render: (size, record) => (
                <span className={`pack-size-badge ${size > 1 ? 'pack-highlight' : ''}`}>
                    {size > 1 ? `×${size}` : '×1'}
                </span>
            ),
        },
        {
            title: 'Cantidad Calculada',
            dataIndex: 'calculated_qty',
            key: 'calculated_qty',
            width: 160,
            sorter: true,
            align: 'right',
            render: (qty) => (
                <span className={`calculated-qty ${getStockClass(qty)}`}>
                    {formatNumber(qty)}
                </span>
            ),
        },
        {
            title: 'Tipo',
            dataIndex: 'is_pack_product',
            key: 'is_pack_product',
            width: 100,
            align: 'center',
            render: (isPack) => isPack ? (
                <Tag color="cyan">PACK</Tag>
            ) : (
                <Tag color="default">UNIDAD</Tag>
            ),
        },
        {
            title: 'Verificado',
            dataIndex: 'verificado',
            key: 'verificado',
            width: 110,
            align: 'center',
            render: (verified) => verified ? (
                <span className="verified-badge">
                    <CheckCircleOutlined /> Sí
                </span>
            ) : (
                <span className="not-verified-badge">Pendiente</span>
            ),
        },
        {
            title: 'Acciones',
            key: 'actions',
            width: 100,
            align: 'center',
            fixed: 'right',
            render: (_, record) => (
                <Tooltip title="Editar Stock">
                    <Button
                        type="primary"
                        size="small"
                        icon={<EditOutlined />}
                        onClick={() => {
                            if (isDemoMode) { message.info('Editar producto — disponible en la versión completa del sistema'); return; }
                            handleEditProduct(record);
                        }}
                        className="edit-product-btn"
                    >
                        Editar
                    </Button>
                </Tooltip>
            ),
        },
    ];

    return (
        <div className="unified-stock-container">
            {/* Stats Cards */}
            {statsLoading ? (
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <Spin size="large" />
                </div>
            ) : stats && (
                <div className="unified-stock-stats">
                    <div className="unified-stat-card">
                        <span className="stat-value">{formatLargeNumber(stats.summary.total_products)}</span>
                        <span className="stat-label">Productos Totales</span>
                        <span className="stat-detail">{formatLargeNumber(stats.summary.products_with_stock)} con stock</span>
                    </div>
                    <div className="unified-stat-card stat-pack">
                        <span className="stat-value">{formatLargeNumber(stats.summary.pack_products_count)}</span>
                        <span className="stat-label">Productos Pack</span>
                        <span className="stat-detail">{stats.summary.verified_pack_products} verificados</span>
                    </div>
                    <div className="unified-stat-card stat-single">
                        <span className="stat-value">{formatLargeNumber(stats.summary.non_pack_products_count)}</span>
                        <span className="stat-label">Productos Individuales</span>
                    </div>
                    <div className="unified-stat-card stat-calculated">
                        <span className="stat-value">{formatLargeNumber(stats.summary.total_calculated_items)}</span>
                        <span className="stat-label">Items Calculados</span>
                        <span className="stat-detail">Unidades totales</span>
                    </div>
                    <div className="unified-stat-card stat-verified">
                        <span className="stat-value">{formatLargeNumber(stats.summary.total_verified)}</span>
                        <span className="stat-label">Verificados</span>
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="unified-stock-filters">
                <div className="unified-stock-filters-row">
                    <div className="filter-group">
                        <span className="filter-label">Buscar</span>
                        <Input
                            className="filter-input"
                            placeholder="Código o descripción..."
                            prefix={<SearchOutlined />}
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onPressEnter={handleSearch}
                            allowClear
                            disabled={isDemoMode}
                        />
                    </div>
                    
                    <div className="filter-group">
                        <span className="filter-label">Marca</span>
                        <Input
                            className="filter-input filter-brand"
                            placeholder="Filtrar por marca..."
                            value={brandInput}
                            onChange={(e) => setBrandInput(e.target.value)}
                            onPressEnter={handleSearch}
                            allowClear
                            disabled={isDemoMode}
                        />
                    </div>
                    
                    <div className="filter-group">
                        <span className="filter-label">Tipo de Producto</span>
                        <Select
                            className="filter-select"
                            value={filters.productTypeFilter}
                            onChange={(value) => handleFilterChange('productTypeFilter', value)}
                            disabled={isDemoMode}
                        >
                            <Option value="all">Todos</Option>
                            <Option value="pack">Solo Packs</Option>
                            <Option value="single">Solo Individuales</Option>
                        </Select>
                    </div>
                    
                    <div className="filter-group">
                        <span className="filter-label">&nbsp;</span>
                        <Checkbox
                            checked={filters.includeZeroStock}
                            onChange={(e) => handleFilterChange('includeZeroStock', e.target.checked)}
                            disabled={isDemoMode}
                        >
                            Incluir stock cero
                        </Checkbox>
                    </div>
                    
                    <div className="filter-group">
                        <span className="filter-label">&nbsp;</span>
                        <Checkbox
                            checked={filters.verifiedOnly}
                            onChange={(e) => handleFilterChange('verifiedOnly', e.target.checked)}
                            disabled={isDemoMode}
                        >
                            Solo verificados
                        </Checkbox>
                    </div>
                    
                    <div className="filter-buttons">
                        <Button 
                            type="primary"
                            icon={<SearchOutlined />}
                            onClick={handleSearch}
                            disabled={isDemoMode}
                        >
                            Buscar
                        </Button>
                        <Button 
                            icon={<ClearOutlined />} 
                            onClick={handleClearFilters}
                            disabled={isDemoMode}
                        >
                            Limpiar
                        </Button>
                        <Button 
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                loadProducts();
                                loadStats();
                            }}
                            loading={loading}
                        >
                            Actualizar
                        </Button>
                    </div>
                </div>
            </div>

            {/* Summary Bar */}
            {summary && (
                <div className="unified-summary-bar">
                    <div className="summary-item">
                        <span className="summary-label">Mostrando:</span>
                        <span className="summary-value">{formatNumber(summary.total_products)} productos</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Packs:</span>
                        <span className="summary-value badge-pack">{summary.pack_products_count}</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Individuales:</span>
                        <span className="summary-value badge-single">{summary.non_pack_products_count}</span>
                    </div>
                    <div className="summary-item">
                        <span className="summary-label">Items Total:</span>
                        <span className="summary-value highlight">{formatNumber(summary.total_calculated_items)}</span>
                    </div>
                </div>
            )}

            {/* Table */}
            <div className="unified-stock-table-container">
                <Table
                    rowKey="product_id"
                    columns={columns}
                    dataSource={products}
                    loading={loading}
                    pagination={{
                        ...pagination,
                        showSizeChanger: true,
                        showQuickJumper: true,
                        pageSizeOptions: ['25', '50', '100', '200'],
                        showTotal: (total, range) => 
                            `${range[0]}-${range[1]} de ${total} productos`,
                    }}
                    onChange={handleTableChange}
                    rowClassName={getRowClassName}
                    scroll={{ x: 1200 }}
                    locale={{
                        emptyText: (
                            <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description="No se encontraron productos"
                            />
                        ),
                    }}
                />
            </div>

            {/* Edit Product Modal */}
            <Modal
                title={
                    <span>
                        <EditOutlined style={{ marginRight: 8 }} />
                        Editar Producto
                    </span>
                }
                open={editModalVisible}
                onCancel={handleEditModalClose}
                footer={[
                    <Button key="cancel" onClick={handleEditModalClose}>
                        Cancelar
                    </Button>,
                    <Button
                        key="submit"
                        type="primary"
                        loading={editLoading}
                        onClick={handleEditSubmit}
                    >
                        Guardar Cambios
                    </Button>,
                ]}
                width={600}
                destroyOnClose
                className="edit-product-modal"
            >
                <Spin spinning={editLoading} tip="Cargando detalles del producto...">
                {editingProduct && (
                    <Form
                        form={editForm}
                        layout="vertical"
                        className="edit-product-form"
                    >
                        {/* Product Info Header */}
                        <div className="edit-product-header">
                            <Tag color="blue">{editingProduct.brand_name}</Tag>
                            <Tag color={editingProduct.is_pack_product ? 'cyan' : 'default'}>
                                {editingProduct.is_pack_product ? 'PACK' : 'UNIDAD'}
                            </Tag>
                            {editingProduct.verificado && (
                                <Tag color="green"><CheckCircleOutlined /> Verificado</Tag>
                            )}
                        </div>

                        {/* Read-only fields */}
                        <Form.Item
                            label="Código de Producto"
                            name="product_code"
                        >
                            <Input 
                                disabled 
                                className="code-input"
                            />
                        </Form.Item>

                        <Form.Item
                            label="Marca"
                            name="brand_name"
                        >
                            <Input disabled />
                        </Form.Item>

                        {/* Editable fields */}
                        <Form.Item
                            label="Descripción"
                            name="product_description"
                            rules={[{ required: true, message: 'La descripción es requerida' }]}
                        >
                            <Input.TextArea 
                                rows={2}
                                placeholder="Descripción del producto"
                            />
                        </Form.Item>

                        <div className="edit-stock-row">
                            <Form.Item
                                label="Stock Original (Raw Stock)"
                                name="raw_stock"
                                rules={[{ required: true, message: 'El stock es requerido' }]}
                                className="stock-input-item"
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    placeholder="Cantidad en stock"
                                />
                            </Form.Item>

                            <Form.Item
                                label="Tamaño Pack"
                                name="pack_size"
                                className="pack-size-input-item"
                            >
                                <InputNumber
                                    style={{ width: '100%' }}
                                    disabled
                                    min={1}
                                />
                            </Form.Item>
                        </div>

                        {/* Calculated quantity display */}
                        <div className="calculated-preview">
                            <span className="preview-label">Cantidad Calculada (preview):</span>
                            <span className="preview-value">
                                {formatNumber(
                                    (editForm.getFieldValue('raw_stock') || 0) * 
                                    (editForm.getFieldValue('pack_size') || 1)
                                )}
                            </span>
                            <span className="preview-formula">
                                = {editForm.getFieldValue('raw_stock') || 0} × {editForm.getFieldValue('pack_size') || 1}
                            </span>
                        </div>
                    </Form>
                )}
                </Spin>
            </Modal>
        </div>
    );
};

export default UnifiedStockTab;
